import { Component, lazy, Suspense, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Loader2, Undo2, Redo2, MoreVertical, Maximize2, Minimize2, Download, Upload,
  FlaskConical, AlertTriangle, RefreshCw, Code2, FileJson, Search, PanelLeftOpen, PanelRightOpen,
  ListTree, Play, X, RotateCcw,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import type { TransactionModel, TransactionRoot, TreeNode } from './transaction/types'
import TreeView from './transaction/TreeView'
import DesignCanvas from './transaction/DesignCanvas'
import Inspector from './transaction/Inspector'
import LogicWorkspace from './transaction/LogicWorkspace'
import SettingsWorkspace from './transaction/SettingsWorkspace'
import SearchPalette from './transaction/SearchPalette'
import JsonEditorPanel from './transaction/JsonEditorPanel'
import { logicCount, settingsSections, SETTINGS_NODE_TYPES, topTabOf, type SettingsKey } from './transaction/setupCatalog'
import { loadTransactionObject, saveTransactionObject, generateBusinessLogicFile } from '@/api/designers'
import { useProjectStore } from '@/store/projectStore'
import { useObjectVersionStore } from '@/store/objectVersionStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'

// "Try it" mounts the real Live Preview — loaded on demand only.
const LivePreviewLazyPane = lazy(() => import('@/pages/livepreview/LivePreviewPage'))

type Workspace = 'design' | 'logic' | 'settings' | 'json'

const MAX_UNDO = 50

// Panel open/closed + widths, remembered per browser (a convenience only —
// storage may be unavailable, in which case the defaults apply).
const PANEL_PREFS_KEY = 'twasta.txnDesigner.panels.v1'
interface PanelPrefs { left: boolean; right: boolean; leftWidth: number; rightWidth: number }
const DEFAULT_PANELS: PanelPrefs = { left: true, right: true, leftWidth: 280, rightWidth: 360 }
function loadPanelPrefs(): PanelPrefs {
  try {
    const raw = localStorage.getItem(PANEL_PREFS_KEY)
    if (!raw) return DEFAULT_PANELS
    const p = JSON.parse(raw) as Partial<PanelPrefs>
    return {
      left: typeof p.left === 'boolean' ? p.left : true,
      right: typeof p.right === 'boolean' ? p.right : true,
      leftWidth: typeof p.leftWidth === 'number' ? Math.max(200, Math.min(500, p.leftWidth)) : 280,
      rightWidth: typeof p.rightWidth === 'number' ? Math.max(300, Math.min(640, p.rightWidth)) : 360,
    }
  } catch {
    return DEFAULT_PANELS
  }
}
function savePanelPrefs(p: PanelPrefs) {
  try { localStorage.setItem(PANEL_PREFS_KEY, JSON.stringify(p)) } catch { /* storage unavailable */ }
}

function useNarrow(maxWidth = 760): boolean {
  const query = `(max-width: ${maxWidth}px)`
  const [narrow, setNarrow] = useState(() => {
    try { return window.matchMedia(query).matches } catch { return false }
  })
  useEffect(() => {
    let mq: MediaQueryList
    try { mq = window.matchMedia(query) } catch { return }
    const on = () => setNarrow(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [query])
  return narrow
}

const isTypingTarget = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

function getDefaultModel(name: string): TransactionModel {
  return {
    obj_name: name,
    sql_models: [{
      sql_model: {
        form_title: 'Main Form',
        form_descr: '',
        form_no: '1',
        main_table: name.toUpperCase(),
        columns: [],
        actions: [],
        validations: [],
        joins: { join_predicates: { joins: [] } },
      }
    }],
    actions: [],
    cross_updates: [],
    follow_up_actions: [],
    attach_docs: [],
  }
}

interface TransactionDesignerExtraProps {
  /** Embedded mode only: route an "Open in code editor" request to the
   *  workbench shell's object-scoped Code tab instead of a browser tab.
   *  `relPath` is the src-relative file path, or null when unresolved
   *  (the Code tab then just opens on the object's file list). */
  onOpenCodeFile?: (relPath: string | null) => void
}

export default function TransactionDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
  onOpenCodeFile,
}: EmbeddedDesignerProps & TransactionDesignerExtraProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  // Restore currentProject in store from URL param on refresh
  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [model, setModel] = useState<TransactionModel>(getDefaultModel(objectName))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null)
  const [workspace, setWorkspace] = useState<Workspace>('design')
  const [settingsSection, setSettingsSection] = useState<SettingsKey>('general')
  // The form on the canvas (its record tab / nested strip).
  const [canvasForm, setCanvasForm] = useState(0)
  // Full-screen toggle for the embedded ("Design" tab) usage only.
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [tryItOpen, setTryItOpen] = useState(false)
  const narrow = useNarrow()
  // Width of the workspace area itself — embedded in the workbench it can be
  // far narrower than the window. Below ~1100px a docked inspector would
  // squeeze the canvas/timeline to a sliver, so it floats instead.
  const [areaWidth, setAreaWidth] = useState(0)
  const areaObserver = useRef<ResizeObserver | null>(null)
  const areaRef = useCallback((el: HTMLDivElement | null) => {
    areaObserver.current?.disconnect()
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => {
      const w = Math.round(entries[0]?.contentRect.width || 0)
      setAreaWidth(prev => (prev === w ? prev : w))
    })
    ro.observe(el)
    areaObserver.current = ro
  }, [])
  const tight = !narrow && areaWidth > 0 && areaWidth < 1100
  // Phone: Structure drawer + inspector bottom sheet.
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const { confirm: confirmAsk, confirmDialog } = useConfirm()

  // Each panel hides on its own (Structure `[`, Inspector `]`); widths and
  // open state are remembered. With the inspector hidden, picking something
  // floats it over the canvas until it is closed or docked.
  const [panels, setPanels] = useState<PanelPrefs>(loadPanelPrefs)
  const [floating, setFloating] = useState(false)
  const updatePanels = useCallback((patch: Partial<PanelPrefs>) => {
    setPanels(prev => { const next = { ...prev, ...patch }; savePanelPrefs(next); return next })
  }, [])
  const leftWidth = panels.leftWidth
  const rightWidth = panels.rightWidth

  // Use ref to always have access to the latest model (fixes stale closure bug)
  const modelRef = useRef<TransactionModel>(model)
  modelRef.current = model

  // Snapshot of the last-saved model state used for dirty detection. Set
  // after load and after each successful save so leaving the page only
  // prompts when there are real edits.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(model) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)

  // Reload the model when an AI agent run touches project files so the open
  // designer reflects the agent's edits without a hard browser refresh.
  // Deferred while dirty so in-progress edits are never clobbered.
  const agentRefresh = useAgentRefresh(projectName, isDirty)
  // Make changes inside Try it writes the model on disk; the designer
  // re-reads it when Try it closes, so its own Save never puts the old copy
  // back.
  const [builtNonce, setBuiltNonce] = useState(0)
  const builtInTryIt = useRef(false)

  // Undo/Redo stacks stored in refs to avoid stale closures
  const undoStackRef = useRef<string[]>([])
  const redoStackRef = useRef<string[]>([])
  const [undoCount, setUndoCount] = useState(0)
  const [redoCount, setRedoCount] = useState(0)
  // Undo depth at the last save/load — the distance from it is the number of
  // unsaved changes shown beside Save.
  const [savedDepth, setSavedDepth] = useState(0)

  // Load data
  useEffect(() => {
    const load = async () => {
      if (!projectName || !objectName) return
      setLoading(true)
      // A (re)load replaces the model wholesale (object switch or agent
      // refresh) — stale undo entries would restore a different/older model.
      undoStackRef.current = []
      redoStackRef.current = []
      setUndoCount(0)
      setRedoCount(0)
      try {
        const data = await loadTransactionObject(projectName, objectName)
        let loaded: TransactionModel
        if (data.content) {
          const root = data.content as TransactionRoot
          loaded = root.transaction ? root.transaction : (data.content as TransactionModel)
        } else {
          loaded = getDefaultModel(objectName)
        }
        setModel(loaded)
        setSavedSnapshot(JSON.stringify(loaded))
        setSavedDepth(0)
        setCanvasForm(0)
        // Selection is index-based (formIndex/columnIndex) — after a reload
        // (object switch or agent refresh) the indices may resolve to a
        // different node, so editing through a stale selection could touch
        // the wrong field. Drop it.
        setSelectedNode(null)
      } catch {
        toast.error('Failed to load transaction object')
        const fallback = getDefaultModel(objectName)
        setModel(fallback)
        setSavedSnapshot(JSON.stringify(fallback))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [projectName, objectName, agentRefresh, builtNonce])

  // Handle model changes with undo tracking - uses ref to avoid stale closures
  const handleModelChange = useCallback((newModel: TransactionModel) => {
    // Push current state to undo stack using ref (always latest)
    const currentJson = JSON.stringify(modelRef.current)
    const stack = undoStackRef.current
    stack.push(currentJson)
    if (stack.length > MAX_UNDO) stack.splice(0, stack.length - MAX_UNDO)
    undoStackRef.current = stack
    redoStackRef.current = []
    setUndoCount(stack.length)
    setRedoCount(0)
    setModel(newModel)
  }, [])

  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current
    if (stack.length === 0) return
    const prev = stack.pop()!
    redoStackRef.current.push(JSON.stringify(modelRef.current))
    setUndoCount(stack.length)
    setRedoCount(redoStackRef.current.length)
    setModel(JSON.parse(prev))
  }, [])

  const handleRedo = useCallback(() => {
    const stack = redoStackRef.current
    if (stack.length === 0) return
    const next = stack.pop()!
    undoStackRef.current.push(JSON.stringify(modelRef.current))
    setUndoCount(undoStackRef.current.length)
    setRedoCount(stack.length)
    setModel(JSON.parse(next))
  }, [])

  // Save - uses ref for always-latest model
  const handleSave = useCallback(async (): Promise<boolean> => {
    setSaving(true)
    try {
      const root: TransactionRoot = { transaction: modelRef.current }
      const saved = await saveTransactionObject(projectName, objectName, root)
      setSavedSnapshot(JSON.stringify(modelRef.current))
      setSavedDepth(undoStackRef.current.length)
      // Bump the per-object version so any mounted consumer (smart-page
      // embed preview, embed property panel, …) refetches and shows the
      // new columns / masks / titles immediately.
      useObjectVersionStore.getState().bump('T', projectName, objectName)
      toast.success('Transaction object saved')
      // Save authors the code behind any rule bound to a business-logic
      // function. When that can't happen the rule is bound but empty, and the
      // field will do nothing in live preview — say so here rather than leave
      // it to be discovered there.
      const bl = saved?.business_logic
      if (bl?.error) {
        toast.error(`Business logic not generated: ${bl.error}`)
      }
      // Tell the embedding shell the object now exists on disk: its Live
      // Preview tab is gated on that, answered once when the node was
      // selected, so a first save would otherwise leave the tab missing
      // until the node was reselected.
      onSaved?.()
      return true
    } catch {
      toast.error('Failed to save')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, onSaved])

  // When embedded, report dirty state + register the save fn so the workbench
  // shell can guard navigation away and offer "Save & Continue".
  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    // The designer can be reached either from the workbench (default) or
    // from another designer (e.g. an embed block on a smart page that
    // jumped here via "Edit transaction"). When `returnTo` is set, we
    // hop back to the originating designer and pass its restore state
    // along; the workbench breadcrumb (selectedNodeId) is preserved so
    // an eventual Back from there still lands on the right node.
    const state = (location.state as {
      selectedNodeId?: string
      returnTo?: 'smart-page'
      returnProject?: string
      returnPage?: string
      smartPageSelection?: unknown
      smartPageScrollTop?: number
    } | null) || {}
    const { selectedNodeId, returnTo, returnProject, returnPage, smartPageSelection, smartPageScrollTop } = state
    guard(() => {
      if (returnTo === 'smart-page' && returnProject && returnPage) {
        navigate(
          `/designers/page/${encodeURIComponent(returnProject)}/${encodeURIComponent(returnPage)}`,
          { state: { selectedNodeId, smartPageSelection, smartPageScrollTop } },
        )
        return
      }
      navigate('/workbench', { state: { selectedNodeId } })
    })
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // Open a business-logic event's source file in the code editor. Routes
  // through `guard` so the user is prompted to Save / Discard unsaved changes
  // first; the resolve + (re)generate then runs against the SAVED model. If
  // the file doesn't exist yet, the backend generates the stub (a descriptive
  // header comment, in the per-event language) by reusing
  // MetadataManager.extract_business_logic, and returns the language-correct
  // path so we open exactly that file — where the editor offers to generate
  // the code. Embedded in the workbench, the file opens in the object's own
  // "Code" tab (via onOpenCodeFile); standalone, in a new browser tab.
  const handleOpenCode = useCallback((method?: string) => {
    guard(async () => {
      if (!projectName) return
      let relPath: string | null = null
      const m = (method || '').trim()
      if (m && objectName) {
        try {
          const res = await generateBusinessLogicFile(projectName, objectName, m)
          if (res?.path) {
            relPath = res.path
          } else {
            toast('Could not locate a code file for this function; opening the editor.')
          }
        } catch {
          // fall through to the object's file list / project src root
        }
      }
      if (embedded && onOpenCodeFile) {
        onOpenCodeFile(relPath)
        return
      }
      const url = relPath
        ? `/srceditor/${encodeURIComponent(projectName)}?file=${encodeURIComponent(relPath)}&root=src`
        : `/srceditor/${encodeURIComponent(projectName)}?root=src`
      window.open(url, '_blank', 'noopener')
    })
  }, [projectName, objectName, guard, embedded, onOpenCodeFile])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        handleRedo()
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
      // Panel toggles — only when not typing.
      if (!e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        if (e.key === '[') { e.preventDefault(); togglePanelRef.current('left') }
        if (e.key === ']') { e.preventDefault(); togglePanelRef.current('right') }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleSave, handleUndo, handleRedo])

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const close = () => setMenuOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menuOpen])

  // Export/Import JSON
  const handleExport = useCallback(() => {
    const root: TransactionRoot = { transaction: modelRef.current }
    const blob = new Blob([JSON.stringify(root, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${modelRef.current.obj_name || 'transaction'}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  const handleImport = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const text = await file.text()
        const parsed = JSON.parse(text) as TransactionRoot
        if (parsed.transaction) {
          handleModelChange(parsed.transaction)
          toast.success('JSON imported')
        } else {
          toast.error('Invalid transaction JSON format')
        }
      } catch {
        toast.error('Failed to parse JSON file')
      }
    }
    input.click()
  }, [handleModelChange])

  // Panel resize handlers
  const startResize = useCallback((e: React.MouseEvent, side: 'left' | 'right') => {
    e.preventDefault()
    const startX = e.pageX
    const startWidth = side === 'left' ? leftWidth : rightWidth
    let latest = startWidth
    const onMouseMove = (ev: MouseEvent) => {
      const delta = side === 'left' ? ev.pageX - startX : startX - ev.pageX
      latest = side === 'left'
        ? Math.max(200, Math.min(500, startWidth + delta))
        : Math.max(300, Math.min(640, startWidth + delta))
      setPanels(prev => (side === 'left' ? { ...prev, leftWidth: latest } : { ...prev, rightWidth: latest }))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      updatePanels(side === 'left' ? { leftWidth: latest } : { rightWidth: latest })
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [leftWidth, rightWidth, updatePanels])

  const togglePanel = useCallback((side: 'left' | 'right') => {
    if (narrow) {
      if (side === 'left') setDrawerOpen(o => !o)
      else setSheetOpen(o => !o)
      return
    }
    if (side === 'left') { updatePanels({ left: !panels.left }); return }
    if (floating) { setFloating(false); return }
    if (tight) { setFloating(true); return }
    updatePanels({ right: !panels.right })
  }, [narrow, tight, panels.left, panels.right, floating, updatePanels])
  const togglePanelRef = useRef(togglePanel)
  togglePanelRef.current = togglePanel

  // Open a record setting full width in Settings.
  const openSettings = useCallback((node?: TreeNode) => {
    const key = node ? SETTINGS_NODE_TYPES[node.type] : undefined
    setSettingsSection(key || (node?.type === 'record-settings' ? 'approvals' : 'general'))
    setWorkspace('settings')
    setFloating(false)
    setSheetOpen(false)
  }, [])

  // Every selection goes through here: it follows the form on the canvas, and
  // a hidden inspector floats (or, on a phone, the bottom sheet opens).
  const select = useCallback((node: TreeNode) => {
    if (SETTINGS_NODE_TYPES[node.type]) { openSettings(node); return }
    setSelectedNode(node)
    if (node.formIndex != null) setCanvasForm(node.formIndex)
    if (narrow) { setSheetOpen(true); setDrawerOpen(false) }
    else if (!panels.right || tight) setFloating(true)
  }, [narrow, tight, panels.right, openSettings])

  // Search → the Design workspace with the item selected.
  const pickFromSearch = useCallback((node: TreeNode) => {
    setWorkspace('design')
    select(node)
  }, [select])

  // "Try it": the real Live Preview of the SAVED object, offered after a save
  // when there are unsaved changes.
  const handleTryIt = useCallback(async () => {
    if (isDirty) {
      const ok = await confirmAsk({
        title: 'Save and try it?',
        message: 'Try it runs the saved version of this object. Save your changes first?',
        confirmLabel: 'Save and try it', tone: 'primary',
      })
      if (!ok) return
      const saved = await handleSave()
      if (!saved) return
    }
    setTryItOpen(true)
  }, [isDirty, confirmAsk, handleSave])

  const runUnitTest = useCallback(async () => {
    const ok = await handleSave()
    if (!ok) return
    // Route the unit test through the Ask-AI agent → unit-test-runner
    // sub-agent. The dock listens for this event (see AskAIDock).
    window.dispatchEvent(new CustomEvent('twasta:agent-run-unit-test', {
      detail: {
        project: projectName,
        objName: objectName,
        objType: 'T',
        title: modelRef.current.obj_name || objectName,
      },
    }))
  }, [handleSave, projectName, objectName])

  const unsaved = isDirty ? Math.max(1, Math.abs(undoCount - savedDepth)) : 0

  // Throw away every unsaved edit: back to the last-saved model. Goes through
  // the history, so Undo brings the edits back if this was a slip.
  const handleDiscard = useCallback(async () => {
    if (!isDirty || savedSnapshot === null) return
    const ok = await confirmAsk({
      title: 'Discard unsaved changes?',
      message: `Go back to the last saved version of ${modelRef.current.obj_name || objectName}. `
        + `${unsaved} unsaved change${unsaved === 1 ? '' : 's'} will be dropped — Undo can bring them back until you leave the designer.`,
      confirmLabel: 'Discard changes',
    })
    if (!ok) return
    try {
      const restored = JSON.parse(savedSnapshot) as TransactionModel
      handleModelChange(restored)
      setSavedDepth(undoStackRef.current.length)
      // Indices in the selection may point at columns/forms that no longer exist.
      setSelectedNode(null)
      setFloating(false)
      setSheetOpen(false)
      toast.success('Changes discarded')
    } catch (err) {
      console.error('[transaction designer] discard failed', err)
      toast.error('Could not restore the saved version')
    }
  }, [isDirty, savedSnapshot, confirmAsk, objectName, unsaved, handleModelChange])
  const logicTotal = useMemo(() => (isModelRenderable(model) ? logicCount(model) : 0), [model])
  const settingsOn = useMemo(() => (isModelRenderable(model) ? settingsSections(model).filter(x => x.on).length : 0), [model])
  // Keep the canvas form valid when forms are removed.
  const formCount = model.sql_models?.length || 0
  useEffect(() => {
    if (canvasForm >= formCount && formCount > 0) setCanvasForm(0)
  }, [canvasForm, formCount])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Loading transaction object...</span>
        </div>
      </div>
    )
  }

  // A transaction the designer can draw needs at least one well-formed sql_model.
  // When the model JSON is corrupted/incomplete (e.g. ``sql_models`` missing),
  // the panels would throw "Cannot read properties of undefined (reading 'map')"
  // and blank the screen. Show a readable message that points the user at
  // regenerating, while still exposing the raw JSON so they can inspect/repair.
  if (!isModelRenderable(model)) {
    return (
      <CorruptModelScreen
        objName={objectName}
        project={projectName}
        model={model}
        embedded={embedded}
        onBack={handleBack}
        onModelChange={handleModelChange}
        onExport={handleExport}
      />
    )
  }

  const iconBtn: React.CSSProperties = {
    width: 36, height: 36, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
    borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    color: 'var(--color-text)', flexShrink: 0,
  }
  const rail = (side: 'left' | 'right') => (
    <div style={{
      flex: 'none', width: 44, background: 'var(--color-bg-secondary)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 10, paddingTop: 10,
      [side === 'left' ? 'borderRight' : 'borderLeft']: '1px solid var(--color-border)',
    }}>
      <button type="button" onClick={() => togglePanel(side)}
        aria-label={side === 'left' ? 'Show structure panel' : 'Show inspector panel'}
        title={side === 'left' ? 'Show structure  [' : 'Show inspector  ]'}
        style={{ ...iconBtn, width: 32, height: 32, border: 'none', background: 'var(--color-surface-2)' }}>
        {side === 'left' ? <PanelLeftOpen size={16} /> : <PanelRightOpen size={16} />}
      </button>
      <span style={{ writingMode: 'vertical-rl', fontSize: '0.875rem', color: 'var(--color-text-secondary)', letterSpacing: '0.04em' }}>
        {side === 'left' ? 'Structure' : 'Inspector'}
      </span>
    </div>
  )
  const resizeHandle = (side: 'left' | 'right') => (
    <div
      onMouseDown={(e) => startResize(e, side)}
      role="separator"
      aria-orientation="vertical"
      style={{ width: 6, cursor: 'col-resize', flexShrink: 0, position: 'relative', margin: side === 'left' ? '0 -3px 0 -3px' : '0 -3px', zIndex: 2 }}
      onMouseEnter={e => { const bar = e.currentTarget.firstElementChild as HTMLElement | null; if (bar) bar.style.background = 'var(--color-accent)' }}
      onMouseLeave={e => { const bar = e.currentTarget.firstElementChild as HTMLElement | null; if (bar) bar.style.background = 'var(--color-border)' }}
    >
      <div style={{ position: 'absolute', left: 2, top: 0, bottom: 0, width: 1, background: 'var(--color-border)', transition: 'background 0.2s' }} />
    </div>
  )

  const inspectorProps = {
    model,
    selectedNode,
    onSelect: select,
    onModelChange: handleModelChange,
    onOpenCode: handleOpenCode,
    project: projectName,
    onGuardedNavigate: guard,
    onUndo: handleUndo,
    onOpenSettings: openSettings,
  }

  // The inspector beside a workspace: docked, floating over it, or a rail.
  const sideInspector = (
    narrow ? null
      : panels.right && !tight ? (
        <>
          {resizeHandle('right')}
          <aside aria-label="Inspector" style={{ width: rightWidth, flexShrink: 0, minWidth: 300, overflow: 'visible', position: 'relative', borderLeft: '1px solid var(--color-border)' }}>
            <Inspector {...inspectorProps} variant="docked" onHide={() => togglePanel('right')} />
          </aside>
        </>
      ) : (
        <>
          {rail('right')}
          {floating && selectedNode && (
            <aside role="dialog" aria-label="Inspector" style={{
              position: 'absolute', top: 12, right: 60, width: 380, maxWidth: 'calc(100% - 120px)', bottom: 12,
              zIndex: 30, border: '1px solid var(--color-border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              background: 'var(--color-bg-secondary)', overflow: 'visible',
            }}>
              <Inspector {...inspectorProps} variant="floating"
                onHide={() => setFloating(false)}
                onDock={tight ? undefined : () => { setFloating(false); updatePanels({ right: true }) }} />
            </aside>
          )}
        </>
      )
  )

  const wsTab = (key: Workspace, label: string, badge?: string) => {
    const on = workspace === key
    return (
      <button key={key} type="button" role="tab" aria-selected={on} onClick={() => { setWorkspace(key); setFloating(false) }} style={{
        font: 'inherit', border: 'none', cursor: 'pointer', padding: narrow ? '8px 12px' : '6px 14px', borderRadius: 6,
        background: on ? 'var(--color-bg-secondary)' : 'transparent', color: on ? 'var(--color-text)' : 'var(--color-text-secondary)',
        fontWeight: on ? 600 : 400, boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none', display: 'inline-flex', gap: 6,
        alignItems: 'center', whiteSpace: 'nowrap',
      }}>
        {label}
        {badge && <span style={{ fontSize: '0.8125rem', background: 'var(--color-border)', borderRadius: 10, padding: '0 7px', fontWeight: 400 }}>{badge}</span>}
      </button>
    )
  }

  const discardButton = canEdit && isDirty ? (
    <button
      type="button"
      className="btn btn-secondary btn-sm"
      onClick={() => void handleDiscard()}
      disabled={saving}
      title="Discard all unsaved changes and go back to the last saved version"
      style={{ height: 36 }}
    >
      <RotateCcw size={14} /> Discard
    </button>
  ) : null

  const saveButton = canEdit ? (
    // Always the primary (default) button; enabled only when there is
    // something to save, so its state says at a glance whether work is unsaved.
    <button
      className="btn btn-sm btn-primary"
      onClick={handleSave}
      disabled={saving || !isDirty}
      title={isDirty ? 'Save your changes (Ctrl+S)' : 'No changes to save'}
      style={{ height: 36, padding: '0 18px', fontWeight: 600 }}
    >
      {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
      {saving ? 'Saving...' : 'Save'}
    </button>
  ) : (
    <ReadOnlyBadge />
  )

  return (
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
    }}>
      {/* Header */}
      <header style={{
        background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)', display: 'flex',
        flexWrap: 'wrap', alignItems: 'center', gap: narrow ? 8 : '10px 20px', padding: narrow ? '8px 10px' : '8px 16px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: narrow ? '1 1 0' : '1 1 240px', minWidth: 0 }}>
          {!embedded && (
            <button type="button" style={iconBtn} onClick={handleBack} aria-label="Back to workbench" title="Back">
              <ArrowLeft size={16} />
            </button>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '1.0625rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {model.obj_name || 'Transaction Designer'}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {projectName}
            </div>
          </div>
        </div>

        {narrow && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button type="button" style={{ ...iconBtn, width: 44, height: 44 }} aria-label="Search columns, events and buttons" onClick={() => setSearchOpen(true)}>
              <Search size={18} />
            </button>
            {saveButton}
          </div>
        )}

        <nav aria-label="Workspaces" role="tablist" style={{
          display: 'flex', background: 'var(--color-surface-2)', borderRadius: 8, padding: 3, gap: 2,
          overflowX: 'auto', maxWidth: '100%', order: narrow ? 3 : undefined, flex: narrow ? '1 1 100%' : undefined,
        }}>
          {wsTab('design', 'Design')}
          {wsTab('logic', 'Logic', String(logicTotal))}
          {wsTab('settings', 'Settings', `${settingsOn} on`)}
          {wsTab('json', 'JSON')}
        </nav>

        {!narrow && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 420px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search columns, events and buttons (Ctrl+K)" style={{
              display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--color-border)', borderRadius: 6, padding: '0 10px',
              height: 36, background: 'var(--color-input-bg)', flex: '0 1 260px', minWidth: 160, cursor: 'text',
              color: 'var(--color-text-secondary)', font: 'inherit', fontSize: '0.9375rem',
            }}>
              <Search size={14} />
              <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Search columns, events, buttons</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', border: '1px solid var(--color-border)', borderRadius: 4, padding: '1px 5px' }}>⌘K</span>
            </button>
            <button type="button" style={{ ...iconBtn, opacity: undoCount === 0 ? 0.45 : 1 }} onClick={handleUndo} disabled={undoCount === 0} aria-label="Undo" title="Undo (Ctrl+Z)">
              <Undo2 size={16} />
            </button>
            <button type="button" style={{ ...iconBtn, opacity: redoCount === 0 ? 0.45 : 1 }} onClick={handleRedo} disabled={redoCount === 0} aria-label="Redo" title="Redo (Ctrl+Y)">
              <Redo2 size={16} />
            </button>
            {unsaved > 0 && (
              <span style={{ fontSize: '0.875rem', color: 'var(--color-warning, #8e4b00)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-warning, #d2a106)' }} />
                {unsaved} unsaved change{unsaved === 1 ? '' : 's'}
              </span>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              title="Save, then unit-test this object via the Ask-AI agent (it fixes test data itself and asks before changing the model)"
              disabled={saving}
              onClick={() => void runUnitTest()}
              style={{ height: 36 }}
            >
              <FlaskConical size={14} /> Run unit test
            </button>
            {discardButton}
            {saveButton}
            <div style={{ position: 'relative' }}>
              <button type="button" style={iconBtn} aria-label="More: export, import, full screen" aria-haspopup="menu"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}>
                <MoreVertical size={16} />
              </button>
              {menuOpen && (
                <div role="menu" style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4,
                  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                  borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  minWidth: 200, zIndex: 100, overflow: 'hidden', padding: 4,
                }}>
                  <MenuButton icon={<Download size={14} />} label="Export JSON" onClick={handleExport} />
                  <MenuButton icon={<Upload size={14} />} label="Import JSON" onClick={handleImport} />
                  {embedded && !hostFullscreen && (
                    <>
                      <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 0' }} />
                      <MenuButton
                        icon={embedFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        label={embedFullscreen ? 'Restore' : 'Full screen'}
                        onClick={() => setEmbedFullscreen((f) => !f)}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Workspaces. Wrapped in a boundary so a render crash from a
          subtly-corrupted model (one that passes the structural guard above
          but breaks deeper in a panel) shows a readable message instead of a
          blank screen. The header's Export/Import JSON menu and the JSON
          workspace sit outside the canvas, so JSON recovery stays available. */}
      <DesignerErrorBoundary objName={objectName}>
      <div ref={areaRef} style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', background: 'var(--color-bg)' }}>
        {workspace === 'design' && (
          <>
            {!narrow && (panels.left ? (
              <>
                <aside aria-label="Structure" style={{ width: leftWidth, minWidth: 200, flexShrink: 0, overflow: 'hidden', borderRight: '1px solid var(--color-border)' }}>
                  <TreeView
                    model={model}
                    selectedNode={selectedNode}
                    onSelect={select}
                    onModelChange={canEdit ? handleModelChange : undefined}
                    onUndo={handleUndo}
                    onHide={() => togglePanel('left')}
                  />
                </aside>
                {resizeHandle('left')}
              </>
            ) : rail('left'))}
            <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
              {narrow && (
                <div style={{ display: 'flex', gap: 8, padding: '8px 8px 0' }}>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: 44 }} onClick={() => setDrawerOpen(true)}>
                    <ListTree size={16} /> Structure
                  </button>
                </div>
              )}
              <DesignCanvas
                model={model}
                selectedNode={selectedNode}
                onSelect={select}
                onModelChange={handleModelChange}
                formIndex={canvasForm}
                onFormIndex={(fi) => setCanvasForm(fi)}
                project={projectName}
                objectTitle={model.obj_name || objectName}
                canEdit={canEdit}
                onTryIt={() => void handleTryIt()}
                onOpenLogic={() => setWorkspace('logic')}
                onOpenSettings={openSettings}
                compact={narrow}
              />
            </main>
            {sideInspector}
          </>
        )}

        {workspace === 'logic' && (
          <LogicWorkspace
            model={model}
            selectedNode={selectedNode}
            onSelect={select}
            onOpenSettings={openSettings}
            compact={narrow}
            inspector={sideInspector}
          />
        )}

        {workspace === 'settings' && (
          <SettingsWorkspace
            model={model}
            section={settingsSection}
            onSection={setSettingsSection}
            onModelChange={handleModelChange}
            onOpenCode={handleOpenCode}
            project={projectName}
            onGuardedNavigate={guard}
            onSelectDesign={(node) => { setWorkspace('design'); select(node) }}
            compact={narrow}
          />
        )}

        {workspace === 'json' && (
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <JsonEditorPanel
              model={model}
              onModelChange={handleModelChange}
            />
          </div>
        )}

        {/* Phone: Structure drawer + inspector bottom sheet */}
        {narrow && drawerOpen && workspace === 'design' && (
          <>
            <div onClick={() => setDrawerOpen(false)} aria-hidden style={{ position: 'absolute', inset: 0, background: 'rgba(22,22,22,0.4)', zIndex: 40 }} />
            <nav aria-label="Structure" style={{
              position: 'absolute', top: 0, bottom: 0, left: 0, width: 'min(324px, 88vw)', zIndex: 41,
              boxShadow: '8px 0 24px rgba(0,0,0,0.18)',
            }}>
              <TreeView
                model={model}
                selectedNode={selectedNode}
                onSelect={select}
                onModelChange={canEdit ? handleModelChange : undefined}
                onUndo={handleUndo}
                onHide={() => setDrawerOpen(false)}
                drawer
              />
            </nav>
          </>
        )}
        {narrow && sheetOpen && selectedNode && (workspace === 'design' || workspace === 'logic') && (
          <>
            <div onClick={() => setSheetOpen(false)} aria-hidden style={{ position: 'absolute', inset: 0, background: 'rgba(22,22,22,0.3)', zIndex: 40 }} />
            <section role="dialog" aria-label="Inspector" style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: '78%', zIndex: 41,
              borderRadius: '14px 14px 0 0', overflow: 'hidden', boxShadow: '0 -8px 24px rgba(0,0,0,0.18)',
              background: 'var(--color-bg-secondary)', display: 'flex', flexDirection: 'column',
            }}>
              <div aria-hidden style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--color-border)', margin: '8px auto 0', flexShrink: 0 }} />
              <div style={{ flex: 1, minHeight: 0 }}>
                <Inspector {...inspectorProps} variant="sheet" onHide={() => setSheetOpen(false)} />
              </div>
            </section>
          </>
        )}
      </div>
      </DesignerErrorBoundary>

      {narrow && (
        <nav aria-label="Actions" style={{
          display: 'flex', justifyContent: 'space-around', borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)', padding: '4px 8px', flexShrink: 0,
        }}>
          <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44, minWidth: 44 }} onClick={handleUndo} disabled={undoCount === 0} aria-label="Undo"><Undo2 size={18} /></button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44, minWidth: 44 }} onClick={handleRedo} disabled={redoCount === 0} aria-label="Redo"><Redo2 size={18} /></button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44 }} onClick={() => void handleTryIt()}><Play size={14} /> Try it</button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44 }} disabled={saving} onClick={() => void runUnitTest()}><FlaskConical size={14} /> Unit test</button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 44, minWidth: 44 }} aria-label="More: export, import" onClick={() => setMenuOpen(o => !o)}><MoreVertical size={18} /></button>
          {menuOpen && (
            <div role="menu" style={{
              position: 'fixed', right: 8, bottom: 60, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', minWidth: 200, zIndex: 100, padding: 4,
            }}>
              {canEdit && isDirty && (
                <MenuButton icon={<RotateCcw size={14} />} label="Discard changes" onClick={() => { setMenuOpen(false); void handleDiscard() }} />
              )}
              <MenuButton icon={<Download size={14} />} label="Export JSON" onClick={handleExport} />
              <MenuButton icon={<Upload size={14} />} label="Import JSON" onClick={handleImport} />
            </div>
          )}
        </nav>
      )}

      <SearchPalette model={model} open={searchOpen} onClose={() => setSearchOpen(false)} onPick={pickFromSearch} fullScreen={narrow} />

      {tryItOpen && (
        <div role="dialog" aria-label="Try it as a user" style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)', flexShrink: 0,
          }}>
            <Play size={14} style={{ color: 'var(--color-accent)' }} />
            <span style={{ fontWeight: 600 }}>Try it — {model.obj_name || objectName}</span>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>the saved version, as a user sees it</span>
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => {
              setTryItOpen(false)
              if (builtInTryIt.current) { builtInTryIt.current = false; setBuiltNonce((n) => n + 1) }
            }}>
              <X size={14} /> Back to the designer
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 12 }}>
            <Suspense fallback={<div style={{ padding: 24, color: 'var(--color-text-secondary)' }}>Loading…</div>}>
              <LivePreviewLazyPane project={projectName} objName={objectName}
                onBuildChange={() => { builtInTryIt.current = true }} />
            </Suspense>
          </div>
        </div>
      )}

      {confirmDialog}

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />
    </div>
  )
}

// ---- Corruption guards ----

// Whether the loaded model has the minimum structure the designer panels assume
// (TreeView/PropertyPanel map over ``sql_models`` unconditionally on first
// render). A model that fails this is corrupted/incomplete and must be
// regenerated — rendering it would throw and blank the screen.
function isModelRenderable(m: TransactionModel | null | undefined): boolean {
  return (
    !!m &&
    Array.isArray(m.sql_models) &&
    m.sql_models.length > 0 &&
    m.sql_models.every((s) => !!s && typeof s === 'object' && !!s.sql_model)
  )
}

// Friendly full-screen shown when the model can't be drawn. Offers the two
// recovery paths: regenerate the object (the proper fix), or inspect/repair the
// raw JSON inline (for an author who knows what broke).
function CorruptModelScreen({
  objName, project, model, embedded, onBack, onModelChange, onExport,
}: {
  objName: string
  project: string
  model: TransactionModel
  embedded: boolean
  onBack: () => void
  onModelChange: (m: TransactionModel) => void
  onExport: () => void
}) {
  const [showJson, setShowJson] = useState(false)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid var(--color-border)', flexShrink: 0,
      }}>
        <div className="flex items-center gap-3">
          {!embedded && (
            <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /></button>
          )}
          <FileJson size={18} style={{ color: 'var(--color-accent)' }} />
          <div>
            <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, margin: 0 }}>{objName}</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem', margin: 0 }}>{project}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-ghost btn-sm" onClick={onExport}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> Export JSON
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowJson(v => !v)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Code2 size={14} /> {showJson ? 'Hide JSON' : 'Edit raw JSON'}
          </button>
        </div>
      </div>

      {showJson ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <JsonEditorPanel model={model} onModelChange={onModelChange} />
        </div>
      ) : (
        <div role="alert" style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center',
          color: 'var(--color-text)',
        }}>
          <AlertTriangle size={40} color="#f59e0b" />
          <div style={{ fontWeight: 600, fontSize: '1.1125rem' }}>
            This transaction can't be opened in the designer
          </div>
          <div style={{
            maxWidth: 560, fontSize: '0.9425rem', lineHeight: 1.55,
            color: 'var(--color-text-secondary)',
          }}>
            The design data for <strong>{objName}</strong> is corrupted or incomplete
            — it has no usable form definition, so the designer can't build the
            screen. The safest fix is to <strong>regenerate this transaction</strong>{' '}
            (via Generate App / the Ask-AI agent). If you know what broke, use{' '}
            <strong>Edit raw JSON</strong> above to inspect and repair it directly.
          </div>
        </div>
      )}
    </div>
  )
}

// Catches render crashes from a model that passes the top-level structural guard
// but still breaks deeper in a panel. Shows a readable message instead of a
// blank screen; resets when the open object changes so a healthy object renders.
interface DesignerErrorBoundaryProps { objName: string; children: ReactNode }
interface DesignerErrorBoundaryState { error: Error | null }
class DesignerErrorBoundary extends Component<DesignerErrorBoundaryProps, DesignerErrorBoundaryState> {
  state: DesignerErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): DesignerErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[transaction designer "${this.props.objName}"] render failed:`, error, info)
  }

  componentDidUpdate(prev: DesignerErrorBoundaryProps) {
    if (prev.objName !== this.props.objName && this.state.error) {
      this.setState({ error: null })
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div role="alert" style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center',
          color: 'var(--color-text)', borderTop: '1px solid var(--color-border)',
        }}>
          <AlertTriangle size={40} color="#f59e0b" />
          <div style={{ fontWeight: 600, fontSize: '1.1125rem' }}>
            This transaction screen could not be displayed
          </div>
          <div style={{
            maxWidth: 560, fontSize: '0.9425rem', lineHeight: 1.55,
            color: 'var(--color-text-secondary)',
          }}>
            The design data for <strong>{this.props.objName}</strong> appears to be
            corrupted or incomplete, so part of the designer failed to render. Try
            regenerating this transaction, or use the <strong>Export / Import JSON</strong>{' '}
            menu above to inspect and repair the model.
          </div>
          <div style={{
            fontFamily: 'monospace', fontSize: '0.8125rem', maxWidth: 560,
            color: 'var(--color-text-secondary)', wordBreak: 'break-word', opacity: 0.8,
          }}>
            {this.state.error.message || 'Unexpected rendering error'}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => this.setState({ error: null })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ---- Helper components ----

function MenuButton({ icon, label, onClick, active }: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 12px',
        fontSize: '0.8625rem',
        color: active ? 'var(--color-accent)' : 'var(--color-text)',
        background: active ? 'var(--color-accent-weak)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg)')}
      onMouseLeave={e => (e.currentTarget.style.background = active ? 'var(--color-accent-weak)' : 'transparent')}
    >
      {icon}
      {label}
    </button>
  )
}
